import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faReceipt,
  faFileCsv,
  faChevronLeft,
  faChevronRight,
  faEye,
  faRefresh,
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { streetlearnApi } from '../api';
import { showToast } from '../../components/toast';
import { API_URL } from '../../config';

const OrdersList = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);

  // Filters
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const data = await streetlearnApi.getOrders({
        page,
        limit,
        q: search.trim() || undefined,
        status: status || undefined,
        from: from || undefined,
        to: to || undefined,
      });
      setOrders(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      showToast.error(err.response?.data?.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, status, from, to]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleExportCsv = () => {
    const token = localStorage.getItem('authToken');
    const params = new URLSearchParams();
    if (search.trim()) params.append('q', search.trim());
    if (status) params.append('status', status);
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    if (token) params.append('token', token);

    window.open(`${API_URL}streetlearn/orders/export?${params.toString()}`, '_blank');
  };

  const totalPages = Math.ceil(total / limit) || 1;
  const start = total === 0 ? 0 : (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  return (
    <div className="mt-5">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
        <div>
          <h4 className="panel-title m-0">
            <FontAwesomeIcon icon={faReceipt} className="me-2 text-primary" />
            StreetLearn Orders & Payments
          </h4>
        </div>
        <div className="d-flex align-items-center flex-wrap">
          <button className="btn btn-default btn-sm mx-1" onClick={fetchOrders} title="Refresh">
            <FontAwesomeIcon icon={faRefresh} spin={loading} /> Refresh
          </button>
          <button className="btn btn-default btn-sm mx-1" onClick={handleExportCsv}>
            <FontAwesomeIcon icon={faFileCsv} /> Export Orders CSV
          </button>

          {total > 0 && (
            <div className="d-inline-flex align-items-center ms-2">
              <span style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }} className="me-2">
                {start} to {end} of {total}
              </span>
              <button
                className="btn btn-default btn-sm"
                title="Previous"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              &nbsp;
              <button
                className="btn btn-default btn-sm"
                title="Next"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Main Table Panel (Exact Subscriber Module UI) */}
      <div className="panel panel-default with-rounded-corner with-shadow mb-3">
        <div className="panel-body p-0">
          <div className="table-responsive">
            <table className="table table-condensed table-hover bg-white sticky-table m-0">
              <thead>
                <tr>
                  <th>Learner Details</th>
                  <th style={{ minWidth: '120px' }}>Product</th>
                  <th style={{ minWidth: '110px' }}>Amount</th>
                  <th style={{ minWidth: '120px' }}>Status</th>
                  <th>Razorpay Details</th>
                  <th style={{ minWidth: '130px' }}>Entitlement</th>
                  <th style={{ minWidth: '140px' }}>Date</th>
                  <th style={{ width: '80px', textAlign: 'center' }}>Action</th>
                </tr>
                {/* In-Table Filter Row */}
                <tr>
                  <th>
                    <input
                      type="text"
                      placeholder=""
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(1);
                      }}
                      style={{ width: '100%', padding: '5px', fontSize: '12px' }}
                    />
                  </th>
                  <th></th>
                  <th></th>
                  <th>
                    <select
                      value={status}
                      onChange={(e) => {
                        setStatus(e.target.value);
                        setPage(1);
                      }}
                      style={{ width: '100%', padding: '5px', fontSize: '12px' }}
                    >
                      <option value="">--select--</option>
                      <option value="paid">Paid</option>
                      <option value="created">Pending</option>
                      <option value="failed">Failed</option>
                      <option value="refunded">Refunded</option>
                    </select>
                  </th>
                  <th></th>
                  <th></th>
                  <th></th>
                  <th style={{ textAlign: 'center' }}>
                    <button
                      type="button"
                      className="btn btn-default btn-xs"
                      title="Clear all filters"
                      onClick={() => {
                        setSearch('');
                        setStatus('');
                        setFrom('');
                        setTo('');
                        setPage(1);
                      }}
                    >
                      Clear
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="text-center py-4">
                      <FontAwesomeIcon icon={faRefresh} spin className="me-2 text-primary" />
                      Loading orders...
                    </td>
                  </tr>
                ) : orders.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-4 text-muted">
                      No orders found matching the filter criteria.
                    </td>
                  </tr>
                ) : (
                  orders.map((o) => (
                    <tr key={o.id}>
                      <td>
                        <div className="font-bold text-dark">{o.learner_phone}</div>
                        {o.learner_name && (
                          <div className="text-muted small">{o.learner_name}</div>
                        )}
                        {o.learner_email && (
                          <div className="text-muted small">{o.learner_email}</div>
                        )}
                      </td>
                      <td>
                        <span className="font-bold text-capitalize">{o.product}</span>
                      </td>
                      <td>
                        <span className="font-bold text-primary" style={{ fontSize: '13px' }}>
                          ₹{Number(o.amount_inr || o.amount_paise / 100).toFixed(2)}
                        </span>
                      </td>
                      <td>
                        {o.status === 'paid' ? (
                          <span className="text-success font-weight-bold">Paid</span>
                        ) : o.status === 'created' ? (
                          <span className="text-warning font-weight-bold">Pending</span>
                        ) : o.status === 'refunded' ? (
                          <span className="text-info font-weight-bold">Refunded</span>
                        ) : (
                          <span className="text-danger font-weight-bold">{o.status}</span>
                        )}
                      </td>
                      <td className="small">
                        <div>
                          Order: <code className="text-dark">{o.razorpay_order_id}</code>
                        </div>
                        {o.razorpay_payment_id && (
                          <div>
                            Payment: <code className="text-muted">{o.razorpay_payment_id}</code>
                          </div>
                        )}
                      </td>
                      <td>
                        {o.has_active_entitlement ? (
                          <span className="text-success font-weight-bold">Active Access</span>
                        ) : o.status === 'paid' ? (
                          <span className="text-warning font-weight-bold">Revoked / Pending</span>
                        ) : (
                          <span className="text-muted small">None</span>
                        )}
                      </td>
                      <td className="text-muted small">
                        {o.paid_at
                          ? new Date(o.paid_at).toLocaleString()
                          : o.created_at
                          ? new Date(o.created_at).toLocaleString()
                          : '-'}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          className="btn btn-default btn-xs"
                          title="View Learner Profile"
                          onClick={() => navigate(`/streetlearn/learners/${o.learner_id}`)}
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrdersList;
